// Additive helper. Extracted (behaviour-preserving) from `step()` in world.ts
// so bots/lab can predict trajectories with the exact same physics the real
// sim uses, instead of a second hand-tuned copy that could drift out of sync.
import type { Aircraft } from './types.ts';
import { angleDiff, clamp } from './geometry.ts';

export interface AdvanceDims { W: number; H: number }

/**
 * Advance one airborne aircraft (`rollout === 0`) by `dt`: consume waypoints
 * inside the lookahead radius (and skip one that lies inside the turning
 * circle — the orbit-trap fix), turn toward the next waypoint at the type's
 * max turn rate (or circle if holding, or fly straight if there is no path),
 * integrate position, and soft-wrap at the screen edge. Mutates `p` in place.
 *
 * `onPathExhausted` fires the instant the path empties (from lookahead
 * consumption or an orbit-trap skip) while `p.landing` is true, mirroring
 * the exact point in the original frame where `step()` used to attempt a
 * touchdown — callers that care (the real sim) pass a callback that may set
 * `p.rollout`; callers that are only predicting (bots) can omit it.
 *
 * Returns `false` (without moving the aircraft) if the callback put it into
 * landing rollout this frame — the same "stop here" the original inline
 * `continue` produced. Aircraft already in rollout (`p.rollout > 0`) are out
 * of scope for this helper; the sim's own rollout branch handles those.
 */
export function advanceAircraft(
  p: Aircraft,
  dims: AdvanceDims,
  dt: number,
  onPathExhausted?: (p: Aircraft) => void,
): boolean {
  const look = p.type.look;
  while (p.path.length && Math.hypot(p.path[0].x - p.x, p.path[0].y - p.y) < look) {
    p.path.shift();
    if (!p.path.length && p.landing) onPathExhausted?.(p);
  }
  if (p.path.length) {
    const n = p.path[0];
    const R = p.speed / p.type.turn;
    const side = Math.sign(angleDiff(p.ang, Math.atan2(n.y - p.y, n.x - p.x))) || 1;
    const ccx = p.x + Math.cos(p.ang + side * Math.PI / 2) * R;
    const ccy = p.y + Math.sin(p.ang + side * Math.PI / 2) * R;
    if (Math.hypot(n.x - ccx, n.y - ccy) < R * 0.92) {
      p.path.shift();
      if (!p.path.length && p.landing) onPathExhausted?.(p);
    }
  }
  if (p.path.length && p.rollout === 0) {
    const n = p.path[0];
    const target = Math.atan2(n.y - p.y, n.x - p.x);
    const diff = angleDiff(p.ang, target);
    let maxTurn = p.type.turn * dt;
    if (p.landing && p.runway && Math.hypot(p.runway.x1 - p.x, p.runway.y1 - p.y) < 90) maxTurn *= 1.25;
    p.ang += clamp(diff, -maxTurn, maxTurn);
    p.bank += (clamp(diff, -1, 1) - p.bank) * Math.min(1, dt * 5);
  } else if (p.holding) {
    p.ang += p.type.turn * 0.9 * dt;
    p.bank += (0.6 - p.bank) * Math.min(1, dt * 4);
  } else {
    p.bank += (0 - p.bank) * Math.min(1, dt * 3);
  }
  if (p.rollout > 0) return false;
  p.x += Math.cos(p.ang) * p.speed * dt;
  p.y += Math.sin(p.ang) * p.speed * dt;
  const m = 36;
  if (p.x < -m) p.x = dims.W + m - 2; if (p.x > dims.W + m) p.x = -m + 2;
  if (p.y < -m) p.y = dims.H + m - 2; if (p.y > dims.H + m) p.y = -m + 2;
  return true;
}
